// ═══════════════════════════════════════════════════════════════
// Modal: Importación masiva de empleados (wizard 4 pasos)
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { X, Download, Upload, ClipboardList, CheckCircle2 } from 'lucide-react';
import { useEmployeeBulkImport } from '../../../hooks/useEmployeeBulkImport';
import Step1_Download from './steps/Step1_Download';
import Step2_Upload from './steps/Step2_Upload';
import Step3_Preview from './steps/Step3_Preview';
import Step4_Summary from './steps/Step4_Summary';

// ── Stepper header ────────────────────────────────────────────
const STEPS = [
  { num: 1, label: 'Plantilla',      icon: Download },
  { num: 2, label: 'Subir archivo',  icon: Upload },
  { num: 3, label: 'Validación',     icon: ClipboardList },
  { num: 4, label: 'Resultado',      icon: CheckCircle2 },
];

const StepperHeader = ({ currentStep }) => (
  <div className="flex items-center justify-center gap-0 mb-6">
    {STEPS.map((s, idx) => {
      const isCompleted = currentStep > s.num;
      const isActive = currentStep === s.num;
      const Icon = s.icon;

      return (
        <React.Fragment key={s.num}>
          <div className="flex flex-col items-center">
            <div className={`
              flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors
              ${isCompleted
                ? 'bg-blue-600 border-blue-600 text-white'
                : isActive
                  ? 'bg-white border-blue-600 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-400'
              }
            `}>
              {isCompleted
                ? <CheckCircle2 className="w-5 h-5" />
                : <Icon className="w-4 h-4" />
              }
            </div>
            <span className={`text-xs mt-1 font-medium ${
              isActive ? 'text-blue-600' : isCompleted ? 'text-blue-500' : 'text-gray-400'
            }`}>
              {s.label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`h-0.5 w-16 mb-4 mx-1 transition-colors ${
              currentStep > s.num ? 'bg-blue-600' : 'bg-gray-200'
            }`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Modal principal ───────────────────────────────────────────
const EmployeeBulkImportModule = ({ onClose, onSuccess }) => {
  const {
    step, resumen, filas, resultado,
    loading, error,
    downloadTemplate, uploadAndValidate, confirmImport,
    goToStep, reset,
  } = useEmployeeBulkImport();

  const handleClose = () => {
    // Si importó algo, notificar al padre para recargar lista
    if (step === 4 && resultado?.importados > 0) {
      onSuccess?.();
    }
    onClose();
  };

  const handleReset = () => {
    reset();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Importación masiva de empleados
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Importa múltiples empleados desde un archivo Excel
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <StepperHeader currentStep={step} />

          {step === 1 && (
            <Step1_Download
              onDownload={downloadTemplate}
              onNext={() => goToStep(2)}
              loading={loading}
              error={error}
            />
          )}
          {step === 2 && (
            <Step2_Upload
              onUpload={uploadAndValidate}
              onBack={() => goToStep(1)}
              loading={loading}
              error={error}
            />
          )}
          {step === 3 && (
            <Step3_Preview
              resumen={resumen}
              filas={filas}
              onConfirm={confirmImport}
              onBack={() => goToStep(2)}
              loading={loading}
              error={error}
            />
          )}
          {step === 4 && (
            <Step4_Summary
              resultado={resultado}
              resumen={resumen}
              onReset={handleReset}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeBulkImportModule;
